import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Home,
  LayoutDashboard,
  GitBranch,
  Settings,
  Clock,
  Users,
  FileText,
  Shield,
  TrendingUp,
  Rocket,
  Database,
  GraduationCap,
  Wrench,
  X,
  LucideIcon,
} from 'lucide-react';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface NavItem {
  path: string;
  icon: LucideIcon;
  label: string;
}

interface NavSection {
  label: string;
  items: NavItem[];
}

type NavigationItem = NavItem | NavSection;

const Sidebar = ({ isOpen, onClose }: SidebarProps) => {
  const { t } = useTranslation();

  const navigationItems: NavigationItem[] = [
    { path: '/', icon: Home, label: t('nav.home') },
    { path: '/dashboard', icon: LayoutDashboard, label: t('nav.dashboard') },
    {
      label: 'Lead Management',
      items: [
        { path: '/pipeline', icon: GitBranch, label: t('nav.pipeline') },
        { path: '/operating-model', icon: Settings, label: t('nav.operatingModel') },
        { path: '/sla-catalogue', icon: Clock, label: t('nav.slaCatalog') },
      ],
    },
    {
      label: 'Operations',
      items: [
        { path: '/roles', icon: Users, label: t('nav.roles') },
        { path: '/sops', icon: FileText, label: t('nav.sops') },
        { path: '/policies', icon: Shield, label: t('nav.policies') },
      ],
    },
    {
      label: 'Management',
      items: [
        { path: '/performance', icon: TrendingUp, label: t('nav.performance') },
        { path: '/implementation', icon: Rocket, label: t('nav.implementation') },
      ],
    },
    {
      label: 'Resources',
      items: [
        { path: '/crm', icon: Database, label: t('nav.crm') },
        { path: '/training', icon: GraduationCap, label: t('nav.training') },
        { path: '/tools', icon: Wrench, label: t('nav.tools') },
      ],
    },
  ];

  const renderNavItem = (item: NavItem) => {
    const Icon = item.icon;
    return (
      <NavLink
        key={item.path}
        to={item.path}
        onClick={() => window.innerWidth < 1024 && onClose()}
        className={({ isActive }) =>
          `flex items-center space-x-3 rtl:space-x-reverse px-4 py-3 rounded-lg transition-colors ${
            isActive
              ? 'bg-primary-50 text-primary-700 font-medium'
              : 'text-gray-700 hover:bg-gray-100'
          }`
        }
      >
        <Icon className="h-5 w-5 flex-shrink-0" />
        <span className="text-sm">{item.label}</span>
      </NavLink>
    );
  };

  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-16 left-0 rtl:left-auto rtl:right-0 h-[calc(100vh-4rem)] w-64 bg-white border-r rtl:border-l rtl:border-r-0 border-gray-200 overflow-y-auto z-30 transform transition-transform duration-300 ${
          isOpen ? 'translate-x-0' : '-translate-x-full rtl:translate-x-full'
        } lg:translate-x-0`}
      >
        {/* Close button for mobile */}
        <button
          onClick={onClose}
          className="lg:hidden absolute top-4 right-4 rtl:left-4 rtl:right-auto p-2 rounded-md text-gray-500 hover:bg-gray-100"
        >
          <X className="h-5 w-5" />
        </button>

        <nav className="p-4 space-y-6">
          {navigationItems.map((section, index) => (
            <div key={index}>
              {'items' in section ? (
                <>
                  <div className="px-4 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    {section.label}
                  </div>
                  <div className="space-y-1">
                    {section.items.map(renderNavItem)}
                  </div>
                </>
              ) : (
                renderNavItem(section)
              )}
            </div>
          ))}
        </nav>
      </aside>
    </>
  );
};

export default Sidebar;
